import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayoutContainer } from 'components/Layouts/AuthLayout';
import { Typography, Input, Button, Alert, IconGraphy, colors, Loading } from '@leapeasy/ui-kit';
import { Box, Checkbox, FormControlLabel, Link } from '@mui/material';
import validator from 'validator';
import { checkInviteToken, emailVerify, registerStart } from 'store/actions/authActions';
import { failedNotif } from 'store/actions/notifActions';
import { getRoleLabel } from 'constants/userRoles';
import { validatePassword } from 'utils/validations';
import { formatUsPhoneInput, isUsPhoneComplete } from 'utils/phoneFormat';
import { useNotificationMessages } from 'hooks/useNotificationMessages';

const INVITE_TOKEN_QUERY = 'token';

export const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get(INVITE_TOKEN_QUERY);

  const userInfoState = useSelector((state) => state.getIn(['auth', 'userInfo']));
  const userInfo = userInfoState ? userInfoState.toJS() : null;
  const accountExists = userInfoState?.get('accountExists') === true;
  const { successText, failedText, hasSuccess, hasError } = useNotificationMessages();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [email, setEmail] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const lastVerifiedEmailRef = useRef('');

  useEffect(() => {
    if (userInfo?.email) {
      setEmail(userInfo.email);
      setRoleLabel(getRoleLabel(userInfo.role));
      return;
    }

    if (!inviteToken) {
      setValidatingInvite(false);
      return;
    }

    setValidatingInvite(true);
    dispatch(failedNotif(null));
    dispatch(checkInviteToken(inviteToken));
  }, [dispatch, inviteToken, userInfo?.email]);

  useEffect(() => {
    if (!accountExists || !inviteToken) {
      return;
    }
    navigate(`/invite/accept/${inviteToken}`, { replace: true });
  }, [accountExists, inviteToken, navigate]);

  useEffect(() => {
    if (userInfo || hasError) {
      setValidatingInvite(false);
      setVerifyingEmail(false);
    }
  }, [userInfo, hasError]);

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
    lastVerifiedEmailRef.current = '';
  }, []);

  const handleEmailVerify = useCallback(() => {
    const trimmed = email.trim();

    if (inviteToken || userInfo || verifyingEmail || !validator.isEmail(trimmed)) {
      return;
    }

    if (trimmed === lastVerifiedEmailRef.current) {
      return;
    }

    lastVerifiedEmailRef.current = trimmed;
    setVerifyingEmail(true);
    dispatch(failedNotif(null));
    dispatch(emailVerify(trimmed));
  }, [dispatch, email, inviteToken, userInfo, verifyingEmail]);

  useEffect(() => {
    if (hasError) {
      setSubmitting(false);
    }
  }, [hasError]);

  useEffect(() => {
    if (hasSuccess) {
      setSubmitting(false);
    }
  }, [hasSuccess]);

  const passwordValid = validatePassword(password);
  const passwordsMatch = password === confirmPassword;
  const canSubmit =
    userInfo &&
    name.trim() &&
    isUsPhoneComplete(phone) &&
    jobTitle.trim() &&
    email &&
    validator.isEmail(email) &&
    passwordValid &&
    passwordsMatch &&
    agreeTerms &&
    !submitting;

  const onSubmit = useCallback(() => {
    if (!userInfo) {
      return;
    }
    setSubmitting(true);
    dispatch(
      registerStart({
        username: name.trim(),
        phonenumber: phone.trim(),
        jobtitle: jobTitle.trim(),
        email,
        password,
        role: userInfo.role,
        landlordId: userInfo.role === 'll' ? userInfo.landlordId : null,
        inviteToken,
      }),
    );
  }, [dispatch, email, inviteToken, jobTitle, name, password, phone, userInfo]);

  if (!userInfo) {
    if (!inviteToken) {
      return (
        <AuthLayoutContainer showGrayLogo scrollable>
          <Box display="flex" flexDirection="column" gap="24px">
            <Box>
              <Typography variant="h2" style={{ color: colors.purple[900], fontWeight: 500 }}>
                Register
              </Typography>
              <Typography variant="body1" style={{ color: colors.neutral[900] }}>
                Enter your landlord email to continue, or use your invitation link if you were
                invited to a team.
              </Typography>
            </Box>

            <Box
              onFocusOut={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  handleEmailVerify();
                }
              }}
            >
              <Input
                value={email}
                onChange={handleEmailChange}
                label="Email"
                type="text"
                minWidth="100%"
                fullWidth
                size="medium"
                disabled={verifyingEmail}
                icon={<IconGraphy icon="Notifications.Email" />}
                errorType={
                  email && !validator.isEmail(email.trim())
                    ? 'error'
                    : email
                      ? 'success'
                      : undefined
                }
                helperMessage={
                  email && !validator.isEmail(email.trim()) ? 'Enter a valid email address' : ''
                }
                showCopy={false}
                showHelperOnTyping={false}
              />
            </Box>

            {verifyingEmail && (
              <Typography variant="body1" style={{ color: colors.neutral[700] }}>
                Verifying email…
              </Typography>
            )}

            {hasError && (
              <Alert
                type="error"
                variant="contained"
                showBorder
                description={failedText}
                showClose={false}
              />
            )}

            <Box display="flex" gap="12px" flexWrap="wrap">
              <Button variant="primary" size="medium" onClick={() => navigate('/lost-invite')}>
                Find your invite
              </Button>
              <Button variant="tertiary" size="medium" onClick={() => navigate('/login')}>
                Back to login
              </Button>
            </Box>
          </Box>
        </AuthLayoutContainer>
      );
    }

    return (
      <AuthLayoutContainer showGrayLogo scrollable>
        <Box display="flex" flexDirection="column" gap="24px">
          <Typography variant="h2" style={{ color: colors.purple[900], fontWeight: 500 }}>
            Register
          </Typography>
          {validatingInvite && !hasError && (
            <Box display="flex" flexDirection="column" alignItems="center" gap="16px">
              <Loading size="large" />
              <Typography variant="body1" style={{ color: colors.neutral[700] }}>
                Validating your invitation…
              </Typography>
            </Box>
          )}
          {!validatingInvite && (
            <>
              <Alert
                type="error"
                variant="contained"
                showBorder
                description={
                  failedText ||
                  'This registration link is invalid or has expired. Open the invitation from your email, or find your invite again.'
                }
                showClose={false}
              />
              <Box display="flex" gap="12px" flexWrap="wrap">
                <Button variant="primary" size="medium" onClick={() => navigate('/lost-invite')}>
                  Find your invite
                </Button>
                <Button variant="tertiary" size="medium" onClick={() => navigate('/login')}>
                  Back to login
                </Button>
              </Box>
            </>
          )}
          {validatingInvite && hasError && (
            <Alert
              type="error"
              variant="contained"
              showBorder
              description={failedText}
              showClose={false}
            />
          )}
        </Box>
      </AuthLayoutContainer>
    );
  }

  return (
    <AuthLayoutContainer showGrayLogo scrollable>
      <Box display="flex" flexDirection="column" gap="24px">
        <Box>
          <Typography variant="h2" style={{ color: colors.purple[900], fontWeight: 500 }}>
            Register
          </Typography>
          <Typography variant="body1" style={{ color: colors.neutral[900] }}>
            Complete your account to join the Leap portal
          </Typography>
        </Box>

        <Box sx={{ border: '1px solid #F8F4F9', height: '0px' }} />

        <Input
          value={email}
          label="Email"
          type="text"
          minWidth="100%"
          fullWidth
          size="medium"
          disabled
          icon={<IconGraphy icon="Notifications.Email" />}
          showCopy={false}
          showHelperOnTyping={false}
        />

        <Input
          value={roleLabel}
          label="Role"
          type="text"
          minWidth="100%"
          fullWidth
          size="medium"
          disabled
          showCopy={false}
          showHelperOnTyping={false}
        />

        <Input
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          label="Password"
          type="password"
          minWidth="100%"
          fullWidth
          size="medium"
          icon={<IconGraphy icon="Security.Lock" />}
          errorType={password && !passwordValid ? 'error' : password ? 'success' : undefined}
          helperMessage={
            password && !passwordValid
              ? 'Must contain 1 number, 1 special character, and at least 8 characters'
              : ''
          }
          showCopy={false}
          showHelperOnTyping={false}
        />

        <Input
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          label="Confirm password"
          type="password"
          minWidth="100%"
          fullWidth
          size="medium"
          icon={<IconGraphy icon="Security.Lock" />}
          errorType={
            confirmPassword && !passwordsMatch ? 'error' : confirmPassword ? 'success' : undefined
          }
          helperMessage={confirmPassword && !passwordsMatch ? 'Passwords do not match' : ''}
          showCopy={false}
          showHelperOnTyping={false}
        />

        <Input
          value={phone}
          onChange={(e) => setPhone(formatUsPhoneInput(e.target.value))}
          label="Phone number"
          type="tel"
          placeholder="(555) 555-5555"
          minWidth="100%"
          fullWidth
          size="medium"
          errorType={phone && !isUsPhoneComplete(phone) ? 'error' : phone ? 'success' : undefined}
          helperMessage={
            phone && !isUsPhoneComplete(phone) ? 'Enter a 10-digit US phone number' : ''
          }
          showCopy={false}
          showHelperOnTyping={false}
        />

        <Input
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          label="Job title"
          type="text"
          minWidth="100%"
          fullWidth
          size="medium"
          showCopy={false}
          showHelperOnTyping={false}
        />

        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          label="Name"
          type="text"
          minWidth="100%"
          fullWidth
          size="medium"
          showCopy={false}
          showHelperOnTyping={false}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" style={{ color: colors.neutral[900] }}>
              I agree to the{' '}
              <Link
                href="https://www.leapeasy.com/terms-and-conditions/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: colors.purple[700] }}
              >
                Terms &amp; Conditions
              </Link>
            </Typography>
          }
        />

        {hasError && (
          <Alert
            type="error"
            variant="contained"
            showBorder
            description={failedText}
            showClose={false}
          />
        )}

        {hasSuccess && (
          <Alert
            type="success"
            variant="contained"
            showBorder
            description={successText}
            showClose={false}
          />
        )}

        <Button
          variant="primary"
          size="medium"
          onClick={onSubmit}
          disabled={!canSubmit}
          style={{ minWidth: '100%' }}
        >
          {submitting ? 'Creating account…' : 'Continue'}
        </Button>

        <Typography
          onClick={() => navigate('/login')}
          style={{
            alignSelf: 'center',
            textDecoration: 'underline',
            cursor: 'pointer',
            color: colors.purple[700],
          }}
        >
          Already have an account? Sign in
        </Typography>
      </Box>
    </AuthLayoutContainer>
  );
};
